import Component from '@glimmer/component';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { on } from '@ember/modifier';
import { task } from 'ember-concurrency';
import { t } from 'ember-intl';
import { pick } from '@nullvoxpopuli/ember-composable-helpers';
import set from 'ember-set-helper/helpers/set';
import can from 'ember-can/helpers/can';
import {
  HdsPageHeader,
  HdsFormTextInputField,
  HdsFormTextareaField,
  HdsButton,
  HdsButtonSet,
} from '@hashicorp/design-system-components/components';
import {
  CoreLayoutMain,
  BreadcrumbsContainer,
  FlexGrid,
  Flex,
  WithErrors,
} from 'core/components';
import { boxPadding } from 'core/modifiers';

/**
 *
 * Edit form for project objects.
 *
 *
 * ```
 * <Page::Projects::Edit
 *   @model={{this.model}}
 * />
 * ```
 *
 * @class PageProjectsEdit
 *
 */
export default class PageProjectsEdit extends Component {
  /**
   * The current project object
   * @argument model
   * @type {Object}
   */

  @service api;
  @service router;
  @service flashMessages;
  @service intl;

  @tracked name = this.args?.model?.name;
  @tracked description = this.args?.model?.description;

  save = task(async (evt) => {
    // don't submit the form
    evt.preventDefault();

    let {
      model: { id, name, description },
      previousRouteName,
    } = this.args;

    if (this.name !== name) {
      await this.api.resourceManager.project.projectServiceSetName(id, {
        name: this.name,
      });
    }

    if (this.description !== description) {
      await this.api.resourceManager.project.projectServiceSetDescription(id, {
        description: this.description,
      });
    }

    this.flashMessages.success(
      this.intl.t('components.page.projects.create.project-updated')
    );

    this.router.transitionTo(previousRouteName);
  });

  <template>
    <WithErrors @error={{this.save.last.error}} as |FormError FieldError|>
      <CoreLayoutMain>
        <:header>
          <HdsPageHeader as |PH|>
            <PH.Breadcrumb>
              <BreadcrumbsContainer />
            </PH.Breadcrumb>
            <PH.Title>
              {{t
                "components.page.projects.edit.title"
                projectName=@model.name
              }}
            </PH.Title>
          </HdsPageHeader>
        </:header>
        <:alert>
          <FormError />
        </:alert>
        <:body>
          <FlexGrid as |G|>
            <G.Item @xs="12" @sm="12" @md="8" @lg="5">
              <form
                class="hcp-hds-form"
                data-test-form
                {{on "submit" this.save.perform}}
              >
                <Flex @direction="column" @gap="xl">
                  {{! HCP project name }}
                  <div>
                    <FieldError
                      @field="name"
                      @hdsDelegatedError={{true}}
                      as |err|
                    >
                      <HdsFormTextInputField
                        @id="projectName"
                        @isInvalid={{err}}
                        @type="text"
                        @value={{this.name}}
                        name="projectName"
                        data-test-project-name
                        data-test-input
                        {{on "input" (pick "target.value" (set this "name"))}}
                        as |F|
                      >
                        <F.Label>
                          {{t
                            "components.page.projects.create.form.label.project-name"
                          }}
                        </F.Label>
                        <F.HelperText>
                          <ul>
                            <li>
                              {{t
                                "components.page.projects.create.form.label.project-name-may-contain"
                              }}
                            </li>
                            <li>
                              {{t
                                "components.page.projects.create.form.label.project-name-cannot-begin-with"
                              }}
                            </li>
                          </ul>
                        </F.HelperText>
                        {{#if err}}
                          <F.Error>
                            {{err.description}}
                          </F.Error>
                        {{/if}}
                      </HdsFormTextInputField>
                    </FieldError>
                  </div>

                  {{! HCP project description }}
                  <div>
                    <FieldError
                      @field="description"
                      @hdsDelegatedError={{true}}
                      as |err|
                    >
                      <HdsFormTextareaField
                        @id="projectDescription"
                        @isInvalid={{err}}
                        @isOptional={{true}}
                        @type="text"
                        @value={{this.description}}
                        name="projectDescription"
                        data-test-project-description
                        data-test-input
                        {{on
                          "input"
                          (pick "target.value" (set this "description"))
                        }}
                        as |F|
                      >
                        <F.Label>
                          {{t
                            "components.page.projects.create.form.label.project-description"
                          }}
                        </F.Label>
                        <F.HelperText>
                          {{t
                            "components.page.projects.create.form.label.project-description-help"
                          }}
                        </F.HelperText>
                        {{#if err}}
                          <F.Error>
                            {{err.description}}
                          </F.Error>
                        {{/if}}
                      </HdsFormTextareaField>
                    </FieldError>
                  </div>
                </Flex>
                <footer {{boxPadding "xl 0 0 0"}}>
                  <section>
                    <HdsButtonSet>
                      {{#if (can "update project" @projects)}}
                        <HdsButton
                          @color="primary"
                          @text={{t "components.page.projects.edit.action"}}
                          type="submit"
                          data-test-submit
                        />
                      {{/if}}
                      <HdsButton
                        @color="secondary"
                        @route={{@previousRouteName}}
                        @text={{t "components.form.cancel"}}
                        data-test-cancel
                      />
                    </HdsButtonSet>
                  </section>
                </footer>
              </form>
            </G.Item>
          </FlexGrid>
        </:body>
      </CoreLayoutMain>
    </WithErrors>
  </template>
}
